import {
  flushLocalDb,
  getMeta,
  openLocalDb,
} from '../db/local.js';

const SERVER_ID_BACKFILL_META_KEY = 'events_server_id_backfill_v1';
const SERVER_ID_BACKFILL_PENDING = 'pending';
const SERVER_ID_BACKFILL_DONE = 'done';

type SqlTask = {
  statement: string;
  values?: unknown[];
};

export type LegacyServerIdRecoveryDecision =
  | { action: 'skip'; missingConfirmedRows: 0 }
  | { action: 'mark_done'; missingConfirmedRows: 0 }
  | { action: 'resume_replay'; missingConfirmedRows: 0 }
  | { action: 'start_replay'; missingConfirmedRows: number };

/**
 * Pure policy for the one-shot cache recovery. Only server-confirmed rows
 * qualify: optimistic events have rev=NULL and must never trigger a replay.
 */
export function decideLegacyServerIdRecovery(input: {
  marker: string | null;
  missingConfirmedRows: unknown;
}): LegacyServerIdRecoveryDecision {
  if (input.marker === SERVER_ID_BACKFILL_DONE) {
    return { action: 'skip', missingConfirmedRows: 0 };
  }
  if (input.marker === SERVER_ID_BACKFILL_PENDING) {
    return { action: 'resume_replay', missingConfirmedRows: 0 };
  }
  const parsedCount = Number(input.missingConfirmedRows);
  const missingConfirmedRows = Number.isSafeInteger(parsedCount) && parsedCount > 0
    ? parsedCount
    : 0;
  return missingConfirmedRows > 0
    ? { action: 'start_replay', missingConfirmedRows }
    : { action: 'mark_done', missingConfirmedRows: 0 };
}

export function buildPullCursorResetTasks(options: {
  completionMarker?: { key: string; value: string };
} = {}): SqlTask[] {
  const tasks: SqlTask[] = [
    {
      statement:
        `INSERT INTO meta(key,value) VALUES ('last_rev','0')
         ON CONFLICT(key) DO UPDATE SET value=excluded.value`,
    },
    {
      statement:
        `INSERT INTO meta(key,value) VALUES ('last_rev_by_tenant','{}')
         ON CONFLICT(key) DO UPDATE SET value=excluded.value`,
    },
  ];
  if (options.completionMarker) {
    tasks.push({
      statement:
        `INSERT INTO meta(key,value) VALUES (?,?)
         ON CONFLICT(key) DO UPDATE SET value=excluded.value`,
      values: [
        options.completionMarker.key,
        options.completionMarker.value,
      ],
    });
  }
  return tasks;
}

/**
 * Reset both pull cursors without touching events, pending ops or outboxes.
 * The optional marker is committed in the same transaction so a crash cannot
 * create an endless reset loop.
 */
export async function resetPullCursors(options: {
  completionMarker?: { key: string; value: string };
} = {}): Promise<void> {
  const db = await openLocalDb(null);
  const tasks = buildPullCursorResetTasks(options);
  await db.executeTransaction(tasks, true);
  await flushLocalDb();
}

/**
 * Older caches could contain confirmed rows written before events_local gained
 * server_id. Rewind the pull cursor once so sync-pull re-upserts the same
 * client_uuid rows with their authoritative server identity. The stale rows
 * stay available while offline and are removed only after a complete pull.
 */
export async function recoverLegacyEventServerIds(): Promise<LegacyServerIdRecoveryDecision> {
  const marker = await getMeta(SERVER_ID_BACKFILL_META_KEY);
  if (
    marker === SERVER_ID_BACKFILL_DONE
    || marker === SERVER_ID_BACKFILL_PENDING
  ) {
    return decideLegacyServerIdRecovery({
      marker,
      missingConfirmedRows: 0,
    });
  }

  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT COUNT(*) AS count
       FROM events_local
      WHERE rev > 0
        AND (server_id IS NULL OR TRIM(server_id) = '')`,
  );
  const row = result.values?.[0] as { count?: unknown } | undefined;
  const decision = decideLegacyServerIdRecovery({
    marker,
    missingConfirmedRows: row?.count,
  });

  if (decision.action === 'start_replay') {
    await resetPullCursors({
      completionMarker: {
        key: SERVER_ID_BACKFILL_META_KEY,
        value: SERVER_ID_BACKFILL_PENDING,
      },
    });
  } else if (decision.action === 'mark_done') {
    await db.run(
      `INSERT INTO meta(key,value) VALUES (?,?)
       ON CONFLICT(key) DO UPDATE SET value=excluded.value`,
      [SERVER_ID_BACKFILL_META_KEY, SERVER_ID_BACKFILL_DONE],
    );
    await flushLocalDb();
  }
  return decision;
}

function normalizedTenantIds(tenantIds: readonly string[]): string[] {
  return [...new Set(tenantIds.map((value) => value.trim()).filter(Boolean))];
}

export function buildLegacyServerIdFinalizeTasks(
  replayedTenantIds: readonly string[],
): SqlTask[] {
  const tenantIds = normalizedTenantIds(replayedTenantIds);
  if (tenantIds.length === 0) return [];
  const placeholders = tenantIds.map(() => '?').join(',');
  return [
    {
      statement:
        `DELETE FROM events_local
          WHERE rev > 0
            AND (server_id IS NULL OR TRIM(server_id) = '')
            AND tenant_id IN (${placeholders})
            AND EXISTS (
              SELECT 1
                FROM meta
               WHERE key = ?
                 AND value = ?
            )`,
      values: [
        ...tenantIds,
        SERVER_ID_BACKFILL_META_KEY,
        SERVER_ID_BACKFILL_PENDING,
      ],
    },
    {
      statement:
        `UPDATE meta
            SET value = ?
          WHERE key = ?
            AND value = ?
            AND NOT EXISTS (
              SELECT 1
                FROM events_local
               WHERE rev > 0
                 AND (server_id IS NULL OR TRIM(server_id) = '')
            )`,
      values: [
        SERVER_ID_BACKFILL_DONE,
        SERVER_ID_BACKFILL_META_KEY,
        SERVER_ID_BACKFILL_PENDING,
      ],
    },
  ];
}

/**
 * Finalize only after drainPullPages reached its terminal page. The delete and
 * pending→done transition are atomic; optimistic rev=NULL rows are preserved.
 * Only tenants named by the terminal response are cleaned: other cached
 * tenants keep both their data and the pending marker until their own replay.
 */
export async function finalizeLegacyEventServerIdRecovery(
  replayedTenantIds: readonly string[],
): Promise<boolean> {
  const tasks = buildLegacyServerIdFinalizeTasks(replayedTenantIds);
  if (tasks.length === 0) return false;
  if (await getMeta(SERVER_ID_BACKFILL_META_KEY) !== SERVER_ID_BACKFILL_PENDING) {
    return false;
  }
  const db = await openLocalDb(null);
  await db.executeTransaction(tasks, true);
  await flushLocalDb();
  return await getMeta(SERVER_ID_BACKFILL_META_KEY) === SERVER_ID_BACKFILL_DONE;
}
